import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';
import { parseDataLocal } from './descargaDateTime';

export const parseTempoEsperaSeconds = (value) => {
  if (value === null || value === undefined || value === '') return null;

  const normalized = String(value).trim();
  if (!normalized || normalized === '-') return null;

  const isNegative = normalized.startsWith('-');
  const durationValue = normalized.replace(/^H\s+/i, '');
  const timeMatch = durationValue.match(/^-?(?:(\d+)\.)?(\d+):(\d{2}):(\d{2})(?:\.\d+)?$/);

  if (!timeMatch) {
    const numericValue = Number(normalized.replace(',', '.'));
    if (!Number.isFinite(numericValue)) return null;
    return Math.max(0, Math.floor(numericValue));
  }

  if (isNegative) return 0;

  const days = Number(timeMatch[1] || 0);
  const hours = Number(timeMatch[2] || 0);
  const minutes = Number(timeMatch[3] || 0);
  const seconds = Number(timeMatch[4] || 0);

  return Math.max(0, (days * 86400) + (hours * 3600) + (minutes * 60) + seconds);
};

export const formatTempoEspera = (totalSeconds) => {
  const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;

  return [hours, minutes, seconds]
    .map(part => String(part).padStart(2, '0'))
    .join(':');
};

export const getTempoEsperaVariant = (totalSeconds) => {
  const hours = Math.max(0, Number(totalSeconds) || 0) / 3600;

  if (hours >= 24) return 'critical';
  if (hours >= 6) return 'warning';
  return 'success';
};

export const hasSaidaDescarga = (row) => {
  const value = row?.original?.saidaDescargaOriginal ?? row?.original?.raw?.saidaDescarga ?? row?.original?.raw?.SaidaDescarga;
  if (value) return true;

  const formattedValue = String(row?.original?.saidaDescarga || '').trim();
  return !!formattedValue && formattedValue !== '-';
};

export const hasChegadaDescarga = (row) => {
  const value = row?.original?.chegadaDescargaOriginal ?? row?.original?.raw?.chegadaDescarga ?? row?.original?.raw?.ChegadaDescarga;
  if (value) return true;

  const formattedValue = String(row?.original?.chegadaDescarga || '').trim();
  return !!formattedValue && formattedValue !== '-';
};

export const TempoEsperaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 76px;
  padding: 2px 8px;
  border-radius: 4px;
  background: ${({ $variant }) => {
    if ($variant === 'empty') return '#e9ecef';
    if ($variant === 'critical') return '#dc3545';
    if ($variant === 'warning') return '#ffc107';
    return '#28a745';
  }};
  color: ${({ $variant }) => (['warning', 'empty'].includes($variant) ? '#212529' : '#ffffff')};
  font-weight: 600;
  line-height: 1.4;

  ${({ $stacked }) => $stacked && `
    flex-direction: column;
    white-space: normal;
    text-align: center;
    line-height: 1.2;
  `}
`;


const pad = (value) => String(value).padStart(2, '0');

const formatAberturaJanela = (date) => (
  `${pad(date.getDate())}/${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`
);

// Limite do setTimeout (2^31 - 1 ms); acima disso o navegador dispara imediatamente.
const MAX_TIMEOUT_MS = 2147483647;

const TempoEsperaDescargaCell = ({ getValue, row }) => {
  const hasChegada = hasChegadaDescarga(row);
  const hasSaida = hasSaidaDescarga(row);
  const apiSeconds = useMemo(() => parseTempoEsperaSeconds(getValue()), [getValue]);
  const inicioContagemMs = parseDataLocal(row?.original?.dataInicialEsperaDescarga)?.getTime() ?? null;

  const isWaitingNow = () => (
    inicioContagemMs !== null && inicioContagemMs > Date.now() && hasChegada && !hasSaida
  );
  const [aguardandoJanela, setAguardandoJanela] = useState(isWaitingNow);
  const [janelaAbriu, setJanelaAbriu] = useState(false);

  // Depois da abertura a contagem parte do valor da API (ou de zero, se ela ainda nao trouxe nada).
  const initialSeconds = apiSeconds ?? (janelaAbriu ? 0 : null);
  const [elapsedSeconds, setElapsedSeconds] = useState(initialSeconds);
  const shouldCount = hasChegada && !hasSaida && !aguardandoJanela && (initialSeconds > 0 || janelaAbriu);

  useEffect(() => {
    const waiting = isWaitingNow();
    setAguardandoJanela(waiting);
    setJanelaAbriu(false);

    if (!waiting) return undefined;

    const timeoutId = window.setTimeout(() => {
      setAguardandoJanela(false);
      setJanelaAbriu(true);
    }, Math.min(Math.max(0, inicioContagemMs - Date.now()), MAX_TIMEOUT_MS));

    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inicioContagemMs, hasChegada, hasSaida]);

  useEffect(() => {
    setElapsedSeconds(initialSeconds);

    if (!shouldCount) return undefined;

    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      setElapsedSeconds(initialSeconds + Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [initialSeconds, shouldCount]);

  if (!hasChegada) {
    return (
      <TempoEsperaBadge $variant="empty">
        --/--/--
      </TempoEsperaBadge>
    );
  }

  if (aguardandoJanela) {
    const dataInicio = formatAberturaJanela(new Date(inicioContagemMs));
    const chegouForaJanela = row?.original?.chegouForaJanela === true || row?.original?.chegouForaJanela === 'true';
    const rotulo = chegouForaJanela ? 'Aguardando janela' : 'Aguardando agendamento';
    const detalhe = chegouForaJanela ? `abre ${dataInicio}` : dataInicio;
    const textoCompleto = `${rotulo} (${detalhe})`;

    return (
      <TempoEsperaBadge $variant="empty" $stacked title={textoCompleto}>
        <span>{rotulo}</span>
        <span>{detalhe}</span>
      </TempoEsperaBadge>
    );
  }

  if (initialSeconds === null) {
    return <TruncatedText value="-" maxLength={18} />;
  }

  return (
    <TempoEsperaBadge $variant={getTempoEsperaVariant(elapsedSeconds)}>
      {formatTempoEspera(elapsedSeconds)}
    </TempoEsperaBadge>
  );
};

export default TempoEsperaDescargaCell;
